import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  FileText, Users, Award, BarChart3, Plus, Eye, Edit, 
  Loader2, ChevronRight, Building2, Calendar, ExternalLink, Trash2, Mail, ImageIcon, ScrollText, Upload, MessageSquare, Inbox, CheckCheck, CreditCard, Brain, MessageCircle, ChevronDown, Bot, User as UserIcon
} from 'lucide-react';
import { programsAPI, applicationsAPI, metricsAPI, messagesAPI, evaluatorsAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const Dashboard = () => {
  const { language } = useLanguage();
  const t = translations[language].dashboard;
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [programs, setPrograms] = useState([]);
  const [applications, setApplications] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programApplications, setProgramApplications] = useState([]);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [feedbackData, setFeedbackData] = useState({});
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState({});

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      if (isAdmin()) {
        // Admin: fetch all data
        const [programsData, applicationsData, metricsData] = await Promise.all([
          programsAPI.getAll(),
          applicationsAPI.getAll(),
          metricsAPI.getDashboard()
        ]);
        setPrograms(programsData);
        setApplications(applicationsData);
        setMetrics(metricsData);
      } else {
        // Applicant: fetch only their applications using userEmail
        const userApplications = await applicationsAPI.getByUserEmail(user.email);
        setApplications(userApplications);
        
        // Get unique program IDs from applications
        const programIds = [...new Set(userApplications.map(app => app.programId))];
        if (programIds.length > 0) {
          const allPrograms = await programsAPI.getAll();
          const userPrograms = allPrograms.filter(p => programIds.includes(p._id));
          setPrograms(userPrograms);
        }

        // Fetch inbox messages
        try {
          const userId = user._id || user.id;
          const msgs = await messagesAPI.getInbox({ userId, userEmail: user.email });
          setInboxMessages(msgs);
        } catch (e) {
          console.error('Error fetching inbox:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgramApplications = async (programId) => {
    try {
      const apps = await applicationsAPI.getByProgram(programId);
      setProgramApplications(apps);
      setSelectedProgram(programId);
    } catch (error) {
      console.error('Error fetching program applications:', error);
    }
  };

  const fetchFeedback = async (applicationId) => {
    if (feedbackData[applicationId]) {
      setExpandedFeedback(expandedFeedback === applicationId ? null : applicationId);
      return;
    }
    setLoadingFeedback(prev => ({ ...prev, [applicationId]: true }));
    try {
      const data = await evaluatorsAPI.getFeedback(applicationId);
      setFeedbackData(prev => ({ ...prev, [applicationId]: data }));
      setExpandedFeedback(applicationId);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoadingFeedback(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  const handleDeleteApplication = async (applicationId, isAdminDelete = false) => {
    const confirmMsg = language === 'es' 
      ? '¿Estás seguro de que quieres eliminar esta postulación?' 
      : 'Are you sure you want to delete this application?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await applicationsAPI.delete(applicationId, user?.email, isAdminDelete);
      
      toast({
        title: language === 'es' ? '¡Eliminada!' : 'Deleted!',
        description: language === 'es' ? 'La postulación ha sido eliminada.' : 'The application has been deleted.'
      });
      
      // Refresh data
      fetchDashboardData();
      if (selectedProgram) {
        fetchProgramApplications(selectedProgram);
      }
    } catch (error) {
      console.error('Error deleting application:', error);
      toast({
        title: language === 'es' ? 'Error' : 'Error',
        description: error.response?.data?.detail || (language === 'es' ? 'No se pudo eliminar' : 'Could not delete'),
        variant: 'destructive'
      });
    }
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'finalist':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'scoring_in_progress':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'under_review':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      submitted: language === 'es' ? 'Enviada' : 'Submitted',
      under_review: language === 'es' ? 'En Revision' : 'Under Review',
      scoring_in_progress: language === 'es' ? 'En Evaluacion' : 'Scoring in Progress',
      finalist: language === 'es' ? 'Finalista' : 'Finalist',
      accepted: language === 'es' ? 'Aceptada' : 'Accepted',
      rejected: language === 'es' ? 'Rechazada' : 'Rejected'
    };
    return labels[status] || status;
  };

  const statusSteps = ['submitted', 'under_review', 'scoring_in_progress', 'finalist', 'accepted'];
  const getStatusProgress = (status) => {
    if (status === 'rejected') return -1;
    const idx = statusSteps.indexOf(status);
    return idx >= 0 ? idx : 0;
  };

  const stepLabels = {
    submitted: language === 'es' ? 'Enviada' : 'Submitted',
    under_review: language === 'es' ? 'Revision' : 'Review',
    scoring_in_progress: language === 'es' ? 'Evaluacion' : 'Scoring',
    finalist: language === 'es' ? 'Finalista' : 'Finalist',
    accepted: language === 'es' ? 'Aceptada' : 'Accepted'
  };

  // Applicant Dashboard
  const ApplicantDashboard = () => (
    <div className="space-y-8" data-testid="applicant-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {language === 'es' ? 'Mis Postulaciones' : 'My Applications'}
          </h1>
          <p className="text-gray-500 mt-1">
            {language === 'es' 
              ? `Bienvenido, ${user?.name}` 
              : `Welcome back, ${user?.name}`}
          </p>
        </div>
        <Button asChild className="bg-purple-600 text-white hover:bg-purple-700 self-start sm:self-auto">
          <Link to="/apply">
            <Plus className="mr-2 w-4 h-4" /> 
            {language === 'es' ? 'Nueva Postulacion' : 'New Application'}
          </Link>
        </Button>
      </div>

      {/* Stats for Applicant */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="glass-card border-2 border-white/20">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 mb-1">{language === 'es' ? 'Total' : 'Total'}</p>
            <p className="text-2xl font-bold text-gray-900">{applications.length}</p>
          </CardContent>
        </Card>
        <Card className="glass-sidebar border-2 border-yellow-200/50">
          <CardContent className="p-4">
            <p className="text-xs text-yellow-700 mb-1">{language === 'es' ? 'En Revision' : 'Under Review'}</p>
            <p className="text-2xl font-bold text-yellow-700">
              {applications.filter(a => a.status === 'under_review' || a.status === 'submitted').length}
            </p>
          </CardContent>
        </Card>
        <Card className="glass-sidebar border-2 border-purple-200/50">
          <CardContent className="p-4">
            <p className="text-xs text-purple-700 mb-1">{language === 'es' ? 'En Evaluacion' : 'Scoring'}</p>
            <p className="text-2xl font-bold text-purple-700">
              {applications.filter(a => a.status === 'scoring_in_progress').length}
            </p>
          </CardContent>
        </Card>
        <Card className="glass-sidebar border-2 border-blue-200/50">
          <CardContent className="p-4">
            <p className="text-xs text-blue-700 mb-1">{language === 'es' ? 'Finalista' : 'Finalist'}</p>
            <p className="text-2xl font-bold text-blue-700">
              {applications.filter(a => a.status === 'finalist').length}
            </p>
          </CardContent>
        </Card>
        <Card className="glass-sidebar border-2 border-green-200/50">
          <CardContent className="p-4">
            <p className="text-xs text-green-700 mb-1">{language === 'es' ? 'Aceptadas' : 'Accepted'}</p>
            <p className="text-2xl font-bold text-green-700">
              {applications.filter(a => a.status === 'accepted').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Inbox Messages */}
      {inboxMessages.length > 0 && (
        <Card className="glass-sidebar border-2 border-orange-200/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Inbox className="w-5 h-5 text-orange-600" />
              {language === 'es' ? 'Mensajes Recibidos' : 'Inbox Messages'}
              <Badge variant="secondary" className="ml-2">
                {inboxMessages.filter(m => !m.read).length} {language === 'es' ? 'nuevos' : 'new'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {inboxMessages.map((msg, idx) => {
                const isOpen = selectedMessage === msg.messageId;
                return (
                  <div
                    key={idx}
                    data-testid={`inbox-msg-${idx}`}
                    className={`border rounded-lg transition-colors cursor-pointer ${
                      msg.read
                        ? 'border-gray-200 bg-white'
                        : 'border-orange-300 bg-orange-50'
                    }`}
                    onClick={async () => {
                      setSelectedMessage(isOpen ? null : msg.messageId);
                      if (!msg.read) {
                        try {
                          const userId = user._id || user.id;
                          await messagesAPI.markAsRead(msg.messageId, { userId, userEmail: user.email });
                          setInboxMessages(prev => prev.map(m =>
                            m.messageId === msg.messageId ? { ...m, read: true } : m
                          ));
                        } catch (e) {
                          console.error('Error marking as read:', e);
                        }
                      }
                    }}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          {!msg.read && (
                            <span className="w-2.5 h-2.5 rounded-full bg-orange-500 shrink-0" />
                          )}
                          {msg.read && (
                            <CheckCheck className="w-4 h-4 text-gray-400 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <h4 className={`text-sm truncate ${msg.read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>
                              {msg.subject}
                            </h4>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {msg.programName} — {new Date(msg.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      </div>

                      {isOpen && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.body}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Applications List */}
      <Card className="glass-card border-2 border-white/20">
        <CardHeader>
          <CardTitle>
            {language === 'es' ? 'Mis Postulaciones' : 'My Applications'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {applications.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {language === 'es' ? 'No tienes postulaciones' : 'No applications yet'}
              </h3>
              <p className="text-gray-600 mb-4">
                {language === 'es' 
                  ? 'Explora los desafíos disponibles y postula a tu primer desafío' 
                  : 'Explore available challenges and submit your first application'}
              </p>
              <Button asChild className="bg-purple-600 text-white hover:bg-purple-700">
                <Link to="/programs">
                  {language === 'es' ? 'Ver Desafíos' : 'View Challenges'}
                </Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {applications.map((application) => {
                const progress = getStatusProgress(application.status);
                const isRejected = application.status === 'rejected';
                return (
                  <div
                    key={application._id}
                    className="p-5 border-2 border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
                    data-testid={`application-card-${application._id}`}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between mb-4 gap-3">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{application.startupName}</h3>
                          <p className="text-sm text-gray-600">{application.programName}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <Badge className={getStatusBadgeStyle(application.status)}>
                              {getStatusLabel(application.status)}
                            </Badge>
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(application.submittedDate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0 self-start">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/application/${application._id}/edit`)}
                          className="border-purple-300 text-purple-600 hover:bg-purple-50"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          {language === 'es' ? 'Editar' : 'Edit'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/programs/${application.programId}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {language === 'es' ? 'Ver' : 'View'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteApplication(application._id, false)}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Progress Stepper */}
                    {!isRejected && (
                      <div className="flex items-center gap-1 mt-3" data-testid={`progress-${application._id}`}>
                        {statusSteps.map((step, idx) => {
                          const isActive = idx <= progress;
                          const isCurrent = idx === progress;
                          return (
                            <React.Fragment key={step}>
                              <div className="flex flex-col items-center flex-1">
                                <div className={`w-full h-2 rounded-full ${isActive ? 'bg-purple-500' : 'bg-gray-200'} ${isCurrent ? 'ring-2 ring-purple-300' : ''}`} />
                                <span className={`text-[10px] mt-1 ${isActive ? 'text-purple-700 font-medium' : 'text-gray-400'}`}>
                                  {stepLabels[step]}
                                </span>
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                    {isRejected && (
                      <div className="mt-3 p-2 bg-red-50 rounded text-xs text-red-600 text-center">
                        {language === 'es' ? 'Esta postulacion no fue seleccionada en esta oportunidad.' : 'This application was not selected at this time.'}
                      </div>
                    )}

                    {/* Feedback Section */}
                    <div className="mt-3">
                      <button
                        data-testid={`feedback-toggle-${application._id}`}
                        onClick={() => fetchFeedback(application._id)}
                        className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 font-medium transition-colors"
                      >
                        {loadingFeedback[application._id] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <MessageCircle className="w-4 h-4" />
                        )}
                        {language === 'es' ? 'Ver Retroalimentacion' : 'View Feedback'}
                        <ChevronDown className={`w-3 h-3 transition-transform ${expandedFeedback === application._id ? 'rotate-180' : ''}`} />
                      </button>

                      {expandedFeedback === application._id && feedbackData[application._id] && (
                        <div className="mt-3" data-testid={`feedback-content-${application._id}`}>
                          {!feedbackData[application._id].feedback_enabled ? (
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-500">
                              {language === 'es' 
                                ? 'La retroalimentacion no esta habilitada para este desafio.' 
                                : 'Feedback is not enabled for this challenge.'}
                            </div>
                          ) : feedbackData[application._id].evaluations.length === 0 ? (
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-500">
                              {language === 'es' 
                                ? 'Aun no hay retroalimentacion disponible.' 
                                : 'No feedback available yet.'}
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {feedbackData[application._id].evaluations.map((evaluation, evalIdx) => (
                                <div 
                                  key={evalIdx} 
                                  className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                                  data-testid={`feedback-evaluation-${evalIdx}`}
                                >
                                  <div className="flex items-center gap-2 mb-3">
                                    {evaluation.is_ai ? (
                                      <>
                                        {evaluation.ai_profile_avatar ? (
                                          <img src={evaluation.ai_profile_avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
                                        ) : (
                                          <Bot className="w-5 h-5 text-violet-500" />
                                        )}
                                        <span className="text-sm font-semibold text-violet-700">
                                          {evaluation.ai_profile_title?.[language] || evaluation.ai_profile_title?.es || evaluation.evaluatorName}
                                        </span>
                                        <Badge className="bg-violet-100 text-violet-700 text-[10px]">IA</Badge>
                                      </>
                                    ) : (
                                      <>
                                        <UserIcon className="w-5 h-5 text-gray-500" />
                                        <span className="text-sm font-semibold text-gray-800">{evaluation.evaluatorName}</span>
                                      </>
                                    )}
                                  </div>

                                  {evaluation.overall_comment && (
                                    <div className="mb-3 p-3 bg-white rounded border border-gray-100">
                                      <p className="text-xs font-medium text-gray-500 mb-1">
                                        {language === 'es' ? 'Comentario General' : 'Overall Comment'}
                                      </p>
                                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{evaluation.overall_comment}</p>
                                    </div>
                                  )}

                                  {evaluation.criteria_feedback.length > 0 && (
                                    <div className="space-y-2">
                                      {evaluation.criteria_feedback.map((cf, cfIdx) => (
                                        <div key={cfIdx} className="p-2 bg-white rounded border border-gray-100">
                                          <p className="text-xs font-medium text-gray-500">{cf.criterionName}</p>
                                          <p className="text-sm text-gray-700 mt-0.5">{cf.comment}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Admin Dashboard
  const AdminDashboard = () => {
    const sidebarLinks = [
      { to: '/admin/programs', icon: FileText, label: language === 'es' ? 'Desafíos' : 'Challenges', color: 'text-purple-600' },
      { to: '/admin/users', icon: Users, label: language === 'es' ? 'Usuarios' : 'Users', color: 'text-blue-600' },
      { to: '/admin/evaluators', icon: Award, label: language === 'es' ? 'Evaluadores' : 'Evaluators', color: 'text-green-600' },
      { to: '/admin/email-settings', icon: Mail, label: 'Email', color: 'text-orange-600' },
      { to: '/admin/images', icon: ImageIcon, label: language === 'es' ? 'Imagenes' : 'Images', color: 'text-teal-600', testId: 'admin-images-link' },
      { to: '/admin/pages', icon: ScrollText, label: language === 'es' ? 'Paginas' : 'Pages', color: 'text-indigo-600', testId: 'admin-pages-link' },
      { to: '/admin/import-users', icon: Upload, label: language === 'es' ? 'Importar' : 'Import', color: 'text-emerald-600', testId: 'admin-import-link' },
      { to: '/admin/plans', icon: CreditCard, label: language === 'es' ? 'Planes' : 'Plans', color: 'text-rose-600', testId: 'admin-plans-link' },
      { to: '/admin/ai-profiles', icon: Brain, label: language === 'es' ? 'Perfiles IA' : 'AI Profiles', color: 'text-violet-600', testId: 'admin-ai-profiles-link' },
    ];

    return (
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar - horizontal on mobile, vertical on desktop */}
        <aside className="lg:w-56 flex-shrink-0" data-testid="admin-sidebar">
          <div className="lg:sticky lg:top-20">
            <div className="mb-4 lg:mb-6">
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3 px-3 hidden lg:block">
                {language === 'es' ? 'Administracion' : 'Administration'}
              </h2>
              <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0">
                {sidebarLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      data-testid={link.testId}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors whitespace-nowrap"
                    >
                      <Icon className={`w-4 h-4 ${link.color}`} />
                      {link.label}
                    </Link>
                  );
                })}
              </nav>
            </div>
            <div className="border-t border-gray-200 pt-4 px-3 hidden lg:block">
              <p className="text-xs text-gray-400">
                {language === 'es' ? 'Sesion como' : 'Logged in as'}
              </p>
              <p className="text-sm font-medium text-gray-800 truncate">{user?.name}</p>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {language === 'es' ? 'Panel de Administracion' : 'Admin Dashboard'}
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">
              {language === 'es'
                ? 'Resumen general de la plataforma'
                : 'Platform overview'}
            </p>
          </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        <Card className="glass-card border-2 border-white/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  {language === 'es' ? 'Total Desafíos' : 'Total Challenges'}
                </p>
                <p className="text-3xl font-bold text-gray-900">{metrics?.totalPrograms || 0}</p>
              </div>
              <FileText className="w-10 h-10 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-2 border-white/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  {language === 'es' ? 'Desafíos Activos' : 'Active Challenges'}
                </p>
                <p className="text-3xl font-bold text-gray-900">{metrics?.activePrograms || 0}</p>
              </div>
              <Award className="w-10 h-10 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-2 border-white/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  {language === 'es' ? 'Total Postulaciones' : 'Total Applications'}
                </p>
                <p className="text-3xl font-bold text-gray-900">{metrics?.totalApplications || 0}</p>
              </div>
              <Users className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-2 border-white/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  {language === 'es' ? 'Puntaje Promedio' : 'Avg Score'}
                </p>
                <p className="text-3xl font-bold text-gray-900">{metrics?.averageScore || 0}</p>
              </div>
              <BarChart3 className="w-10 h-10 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Programs List with Application Count */}
        <Card className="glass-card border-2 border-white/20">
          <CardHeader>
            <CardTitle>
              {language === 'es' ? 'Desafíos y Postulaciones' : 'Challenges & Applications'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {programs.map((program) => {
                const appCount = applications.filter(a => a.programId === program._id).length;
                return (
                  <div
                    key={program._id}
                    className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedProgram === program._id 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                    onClick={() => fetchProgramApplications(program._id)}
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={program.image || '/placeholder.png'}
                        alt={program.name}
                        className="w-12 h-12 rounded object-cover"
                        onError={(e) => e.target.src = '/placeholder.png'}
                      />
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">{program.name}</h3>
                        <Badge
                          className={`text-xs ${
                            program.status === 'open'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {program.status === 'open' 
                            ? (language === 'es' ? 'Abierto' : 'Open')
                            : program.status === 'closed'
                              ? (language === 'es' ? 'Cerrado' : 'Closed')
                              : (language === 'es' ? 'Borrador' : 'Draft')}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-purple-600">
                        {appCount} {language === 'es' ? 'postulaciones' : 'applications'}
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Applications for Selected Program */}
        <Card className="glass-card border-2 border-white/20">
          <CardHeader>
            <CardTitle>
              {selectedProgram 
                ? (language === 'es' ? 'Postulaciones del Desafío' : 'Challenge Applications')
                : (language === 'es' ? 'Selecciona un Desafío' : 'Select a Challenge')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedProgram ? (
              <div className="text-center py-12 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{language === 'es' 
                  ? 'Haz clic en un desafío para ver sus postulaciones' 
                  : 'Click on a program to view its applications'}
                </p>
              </div>
            ) : programApplications.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{language === 'es' 
                  ? 'No hay postulaciones para este desafío' 
                  : 'No applications for this program'}
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {programApplications.map((app) => (
                  <div
                    key={app._id}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">{app.startupName}</h4>
                        <p className="text-sm text-gray-600">{app.founderName}</p>
                        <p className="text-xs text-gray-500">{app.email}</p>
                      </div>
                      <Badge className={getStatusBadgeStyle(app.status)}>
                        {getStatusLabel(app.status)}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-500">
                        {new Date(app.submittedDate).toLocaleDateString()}
                      </span>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-purple-600 text-white hover:bg-purple-700"
                          onClick={() => navigate(`/admin/application/${app._id}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {language === 'es' ? 'Revisar' : 'Review'}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteApplication(app._id, true)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Applications Table */}
      <Card className="glass-card border-2 border-white/20">
        <CardHeader>
          <CardTitle>
            {language === 'es' ? 'Todas las Postulaciones' : 'All Applications'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{language === 'es' ? 'Startup' : 'Startup'}</TableHead>
                <TableHead>{language === 'es' ? 'Desafío' : 'Challenge'}</TableHead>
                <TableHead>{language === 'es' ? 'Fundador' : 'Founder'}</TableHead>
                <TableHead>{language === 'es' ? 'Fecha' : 'Date'}</TableHead>
                <TableHead>{language === 'es' ? 'Estado' : 'Status'}</TableHead>
                <TableHead>{language === 'es' ? 'Acciones' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {applications.slice(0, 10).map((application) => (
                <TableRow key={application._id}>
                  <TableCell className="font-medium">{application.startupName}</TableCell>
                  <TableCell>{application.programName || 'N/A'}</TableCell>
                  <TableCell>{application.founderName}</TableCell>
                  <TableCell>
                    {new Date(application.submittedDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusBadgeStyle(application.status)}>
                      {getStatusLabel(application.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => navigate(`/admin/application/${application._id}`)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {language === 'es' ? 'Ver' : 'View'}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteApplication(application._id, true)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
        </main>
      </div>
    );
  };

  // Check if user is an evaluator
  const isEvaluator = () => {
    return user?.role === 'evaluator';
  };

  // Check if user is an organization
  const isOrganization = () => {
    return user?.role === 'organization';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eef0f4] flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Redirect evaluators to their specific dashboard
  if (isEvaluator()) {
    return <Navigate to="/evaluator-dashboard" replace />;
  }

  // Redirect organizations to their specific dashboard
  if (isOrganization()) {
    return <Navigate to="/organization-dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-[#eef0f4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isAdmin() ? <AdminDashboard /> : <ApplicantDashboard />}
      </div>
    </div>
  );
};

export default Dashboard;
